/**
 * Purpose:
 *   Renders a single submenu item link.
 */

'use client';

import Link from 'next/link';
import { useMenuRoutePrefetch } from './(hooks)/useMenuRoutePrefetch';
import { queueNextHistoryState } from '@/lib/navigation/historyState';
import { cx } from '@/util/functions/cx';
import { Icon } from '@/components/ui/icon/Icon';
import type { MenuItemConfig } from '@/types/menu';
import type { MouseEvent as ReactMouseEvent } from 'react';
import {
  MENU_SUBMENU_ITEM,
  MENU_SUBMENU_ITEM_ACTIVE,
  MENU_TEXT,
  MENU_TEXT_ACTIVE,
} from './Menu.styles';

export interface IMenuItemSubmenuItemProps {
  child: MenuItemConfig;
  isActive: boolean;
  isFocusable: boolean;
  onNavigate: (href: string) => void;
  onLinkClick?: () => void;
}

function isPlainLeftClick(event: ReactMouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

export function MenuItemSubmenuItem({
  child,
  isActive,
  isFocusable,
  onNavigate,
  onLinkClick,
}: IMenuItemSubmenuItemProps) {
  const prefetchRouteOnIntent = useMenuRoutePrefetch();

  if (!child.href) return null;
  const href = child.href;
  const childTextClasses = isActive ? MENU_TEXT_ACTIVE : MENU_TEXT;

  return (
    <Link
      href={href}
      prefetch={false}
      onMouseEnter={() => prefetchRouteOnIntent(href)}
      onFocus={() => prefetchRouteOnIntent(href)}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) {
          return;
        }

        onNavigate(href);
        onLinkClick?.();

        queueNextHistoryState(child.nextHistoryState);
      }}
      data-testid={`menu-item-${child.id}`}
      className={cx(MENU_SUBMENU_ITEM, isActive && MENU_SUBMENU_ITEM_ACTIVE)}
      aria-current={isActive ? 'page' : undefined}
      tabIndex={isFocusable ? undefined : -1}
    >
      <Icon src={child.icon} size={18} preserveColors aria-hidden />
      <span className={childTextClasses}>{child.label}</span>
    </Link>
  );
}
